import {
  RosterReissueSummary,
  RosterReissueDetailsButton,
} from "./RosterReissueSummary";
import { useRosterChangeNotice } from "../hooks/useRosterChangeNotice";
import {
  RosterViewHeader,
  type RosterViewHeaderProps,
} from "./RosterViewNavigation";
import { Skeleton } from "@/components/ui/skeleton";
import type * as type from "@vTypes";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type SetStateAction,
} from "react";
import TableBodyView from "../../../components/feature/roster_view/TableBodyView";
import { RosterShiftLegendChips } from "@components/feature/rosterTable/RosterShiftLegendChips";
import { useRosterDisplayShiftColumns } from "@/hooks/shift/useRosterDisplayShiftColumns";
import { scheduleDownload_excel } from "@pages/roster-view/scheduleDownload.ts";
import type { Roster, ShiftManage } from "@vTypes/snapshot-type.ts";
import RosterMadeTable from "../../../components/feature/rosterTable/RosterMadeTable";
import type { ShiftLookupMaps } from "@/utils/shift-util";
import type { ShiftCatalogShift } from "@/services/shift/shiftCatalog";
import { useIsAdmin } from "@/hooks/useAuth";
import { useIsHeadNurse } from "@/hooks/useHeadNurse";
import { manPowerMonthlyFromShiftManage } from "@/utils/issuedRosterManpower";
import { useIssuedPrecepteeStats } from "../hooks/useIssuedPrecepteeStats";
import { cn } from "@/lib/utils";
import TableHeader from "@components/feature/rosterTable/TableHeader.tsx";
import {
  buildRosterTableLayoutConfig,
  ROSTER_GRID_GAP_PX,
  ROSTER_DAY_CELL_PX,
} from "@/components/nurse/rosterColumnHoverLayout";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import RosterViewSkeleton from "./RosterViewSkeleton";
import useRosterConfig from "@/hooks/useRosterConfig";
import type { NurseResponse } from "@/types/nurse-type";
import { FileSpreadsheet, Loader2 } from "lucide-react";
import IssuedDate from "./issued-date";
import type { UserData } from "@/types/user-type";
import type { IssuedAssignmentsViewBundle } from "@vTypes/snapshot-type";

import { useTeamsQuery } from "@/hooks/roster-craete/useTeamsQuery";
import type { SnapshotTeam } from "@/utils/groupSnapshotTeams";

function RosterViewAllTabContent({
  headerProps,
  issuedSnapshotId,
  refreshIssuedRoster,
  shiftManager,
  issuedList: _issuedList,
  currentScheduleDate,
  setCurrentScheduleDate: _setCurrentScheduleDate,
  holidayDates,
  selectedGroupId: _selectedGroupId,
  availableShifts,
  shiftLookupMaps,
  rosterSheet: _rosterSheet,
  monthlySchedule,
  issuedMemo = "",
  issuedTeams = null,
  isRosterSnapshotLoading = false,
  /** `Roster_view` outlet — 스냅샷 정렬 시 `snapShotData.created_at` */
  issuedDate,
  issuedAssignmentsView: _issuedAssignmentsView = null,
  issuedSnapshotGroupId = "",
  setIssuedSnapshotGroupId: _setIssuedSnapshotGroupId,
}: {
  headerProps: RosterViewHeaderProps;
  issuedSnapshotId?: number;
  refreshIssuedRoster: () => Promise<{ isError: boolean }>;
  shiftManager: ShiftManage | ShiftManage[];
  issuedList: { year: number; month: number; schedule_id: string }[];
  currentScheduleDate: { year: number; month: number; schedule_id: string };
  setCurrentScheduleDate: (value: {
    year: number;
    month: number;
    schedule_id: string;
  }) => void;
  holidayDates: string[];
  selectedGroupId: string | null;
  availableShifts: type.availableShifts[];
  shiftLookupMaps: ShiftLookupMaps<ShiftCatalogShift>;
  rosterSheet: Roster | null;
  monthlySchedule: unknown[] | null;
  issuedMemo?: string;
  issuedTeams?: readonly SnapshotTeam[] | null;
  isRosterSnapshotLoading?: boolean;
  issuedDate?: Date;
  loginUser?: UserData | null;
  issuedAssignmentsView?: IssuedAssignmentsViewBundle | null;
  issuedSnapshotGroupId?: string;
  setIssuedSnapshotGroupId: (rawGroupId: string) => void;
}) {
  void _selectedGroupId;
  void _rosterSheet;
  const isAdmin = useIsAdmin();
  const isHeadNurse = useIsHeadNurse();
  void _issuedList;
  void _setCurrentScheduleDate;
  void _issuedAssignmentsView;
  void _setIssuedSnapshotGroupId;
  const [teamView, setTeamView] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const downloadInProgress = useRef(false);
  const downloadRoster = async () => {
    if (downloadInProgress.current) return;
    downloadInProgress.current = true;
    setIsDownloading(true);
    try {
      await scheduleDownload_excel(currentScheduleDate);
    } finally {
      downloadInProgress.current = false;
      setIsDownloading(false);
    }
  };
  const { data: legacyTeams } = useTeamsQuery(
    issuedSnapshotGroupId,
    currentScheduleDate.year,
    currentScheduleDate.month,
    {
      enabled:
        issuedTeams === null && !!monthlySchedule && !!issuedSnapshotGroupId,
    }
  );
  const hasTeams = (issuedTeams ?? legacyTeams ?? []).length > 0;
  const [showPerShiftStats, setShowPerShiftStats] = useState(false);

  const { data: rosterConfig, isPending: isRosterConfigPending } =
    useRosterConfig();
  const viewLayoutConfig = useMemo(
    () =>
      buildRosterTableLayoutConfig({
        showPreceptorColumn: Boolean(rosterConfig?.show_preceptor),
        showLevelColumn: Boolean(rosterConfig?.show_level),
        showGradeColumn: false,
        showExperienceColumn: false,
      }),
    [rosterConfig?.show_preceptor, rosterConfig?.show_level]
  );
  /** `use_mid === false`면 미드(M) 시프트 행 제외 — 통계·열·범례 동일 기준 */
  const shiftsForView = useMemo(() => {
    if (rosterConfig?.use_mid !== false) return availableShifts;
    return (availableShifts ?? []).filter((s) => s.default_shift !== "M");
  }, [availableShifts, rosterConfig?.use_mid]);

  /** 상위 스냅샷 데이터 — 별도 미러링 없이 그대로 사용 */
  const baseNurses = useMemo(() => monthlySchedule ?? [], [monthlySchedule]);

  /** TableHeader 열 정렬(이름·경력 등) 시에만 덮어씀. 월/스케줄 바뀌면 상위 데이터로 리셋 */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre_monthlyNurseSchedule 반환 타입
  const [sortedNurses, setSortedNurses] = useState<any[] | null>(null);

  useEffect(() => {
    setSortedNurses(null);
  }, [monthlySchedule, currentScheduleDate.schedule_id]);

  const displayNurses = sortedNurses ?? baseNurses;

  const isUseMid = rosterConfig?.use_mid;
  const { displayShifts: rosterViewShiftLegend } = useRosterDisplayShiftColumns(
    displayNurses as {
      schedule?: (string | null | { code: string } | null)[] | null;
      schedule_ids?: (number | null)[] | null;
    }[],
    shiftsForView,
    isUseMid,
    { includeBaselineMainTemplateColumns: true }
  );

  const setCopyNursesForHeader = useCallback(
    (value: SetStateAction<NurseResponse[]>) => {
      setSortedNurses((prev) => {
        const base = baseNurses;
        return typeof value === "function" ? value(prev ?? base) : value;
      });
    },
    [baseNurses]
  );

  const manPower_monthly = useMemo(
    () => manPowerMonthlyFromShiftManage(shiftManager),
    [shiftManager]
  );

  const precepteeStats = useIssuedPrecepteeStats({
    nurses: displayNurses,
    groupId: issuedSnapshotGroupId,
    year: currentScheduleDate.year,
    month: currentScheduleDate.month,
  });

  const changes = useRosterChangeNotice({
    groupId: issuedSnapshotGroupId ?? "",
    year: currentScheduleDate.year,
    month: currentScheduleDate.month,
    snapshotId: issuedSnapshotId,
    refreshRoster: refreshIssuedRoster,
  });

  const nurseRoles = useMemo(() => {
    const roles = new Map<string, string>();
    for (const nurse of displayNurses as readonly {
      nurse_id: string;
      role?: string | null;
    }[]) {
      const role = nurse.role?.trim().toUpperCase();
      if (role === "RN" || role === "AN")
        roles.set(String(nurse.nurse_id), role);
    }
    return roles;
  }, [displayNurses]);

  if (
    isRosterSnapshotLoading ||
    isRosterConfigPending ||
    changes.isInitialLoading
  ) {
    return (
      <>
        <div className="px-5 pt-6 pb-4">
          <RosterViewHeader {...headerProps} />
        </div>
        <RosterViewSkeleton />
      </>
    );
  }

  return (
    <div className="font-pretendard w-full [container-type:inline-size]">
      <div className="flex flex-col items-center gap-10">
        <div className="relative flex flex-col items-start w-full">
          <div
            className="w-full px-5 bg-white z-20 flex flex-col items-start sticky top-0 pt-6"
            style={
              showPerShiftStats
                ? {
                    minWidth:
                      viewLayoutConfig.bodyFirstColPx +
                      ROSTER_GRID_GAP_PX +
                      new Date(
                        currentScheduleDate.year,
                        currentScheduleDate.month,
                        0
                      ).getDate() *
                        ROSTER_DAY_CELL_PX +
                      51 +
                      40 +
                      rosterViewShiftLegend.length * ROSTER_DAY_CELL_PX +
                      Math.max(0, rosterViewShiftLegend.length - 1) * 5.7 +
                      8,
                  }
                : undefined
            }
          >
            <div className="sticky left-5 mb-4 flex w-[calc(100cqw-2.5rem)] flex-col gap-2">
              <RosterViewHeader {...headerProps} />
            </div>
            <div>
              <div className="flex flex-col ">
                {!isRosterSnapshotLoading && displayNurses.length > 0 && (
                  <section
                    className={cn(
                      "sticky left-5 mb-2 flex w-[calc(100cqw-2.5rem)] flex-wrap items-center justify-between gap-3 rounded-lg border border-[#D6D6DE] bg-white px-3 py-1",
                      changes.notice && "roster-reissue-bar"
                    )}
                    aria-label="보기 설정"
                  >
                    <div className="flex min-w-0 flex-wrap items-center gap-3">
                      <IssuedDate issuedAtDisplay={issuedDate} />
                      <RosterReissueSummary
                        notice={changes.notice}
                        isError={changes.isError}
                        onRetry={changes.retry}
                      />
                    </div>
                    <div className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-3">
                      <RosterReissueDetailsButton
                        nurseRoles={nurseRoles}
                        notice={changes.isError ? undefined : changes.notice}
                      />
                      <button
                        type="button"
                        aria-pressed={precepteeStats.included}
                        disabled={precepteeStats.disabled}
                        onClick={() =>
                          precepteeStats.setIncluded((value) => !value)
                        }
                        title="상단 인원 통계에 프리셉티 기간의 근무를 포함하거나 제외합니다."
                        className={cn(
                          "inline-flex h-7 shrink-0 items-center justify-center rounded-full border px-3 text-xs font-bold transition-colors whitespace-nowrap",
                          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0E59C6]/30 focus-visible:ring-offset-1",
                          precepteeStats.disabled
                            ? "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400"
                            : precepteeStats.included
                              ? "border-[#0E59C6]/45 bg-[#EFF6FF] text-[#0E59C6]"
                              : "border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:text-slate-700"
                        )}
                      >
                        {precepteeStats.label}
                      </button>
                      {hasTeams && (
                        <button
                          type="button"
                          aria-pressed={teamView}
                          onClick={() => setTeamView((value) => !value)}
                          className={cn(
                            "inline-flex h-7 shrink-0 items-center justify-center rounded-full border px-3 text-xs font-semibold shadow-sm transition-colors whitespace-nowrap",
                            teamView
                              ? "border-[#0E59C6] bg-[#EFF6FF] text-[#0E59C6]"
                              : "border-slate-200 bg-white text-slate-600",
                            "hover:border-[#0E59C6]/40 hover:bg-[#F5F8FC] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-offset-1"
                          )}
                        >
                          {teamView ? "전체보기" : "팀별보기"}
                        </button>
                      )}
                      <button
                        type="button"
                        disabled={
                          isDownloading ||
                          isRosterSnapshotLoading ||
                          monthlySchedule == null
                        }
                        aria-busy={isDownloading}
                        onClick={() => void downloadRoster()}
                        className="inline-flex h-7 shrink-0 items-center justify-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600 shadow-sm transition-colors whitespace-nowrap hover:border-[#0E59C6]/40 hover:bg-[#F5F8FC] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-offset-1 disabled:opacity-50"
                      >
                        {isDownloading ? (
                          <Loader2
                            aria-hidden="true"
                            className="size-3.5 animate-spin motion-reduce:animate-none"
                          />
                        ) : (
                          <FileSpreadsheet
                            aria-hidden="true"
                            className="size-3.5"
                          />
                        )}
                        <span>전체 근무표 다운로드</span>
                      </button>
                    </div>
                  </section>
                )}

                {!isRosterSnapshotLoading && displayNurses.length > 0 && (
                  <div
                    className={cn(
                      "relative mb-4 animate-in fade-in-0 duration-300 transition-opacity"
                    )}
                  >
                    {precepteeStats.pending ? (
                      <div
                        role="status"
                        aria-label="프리셉티 통계를 불러오는 중입니다"
                      >
                        <Skeleton className="h-32 w-full" />
                      </div>
                    ) : precepteeStats.isError ? (
                      <div
                        role="alert"
                        className="flex h-32 items-center justify-center gap-3 text-sm text-muted-foreground"
                      >
                        <span>
                          프리셉티 정보를 불러오지 못해 통계를 표시할 수
                          없습니다.
                        </span>
                        <button
                          type="button"
                          className="underline"
                          onClick={() => void precepteeStats.refetch()}
                        >
                          다시 시도
                        </button>
                      </div>
                    ) : (
                      <RosterMadeTable
                        layoutConfig={viewLayoutConfig}
                        gridType="ROSTER_ONLY_VIEW"
                        defaultShiftCode={shiftsForView}
                        year={currentScheduleDate.year}
                        month={currentScheduleDate.month}
                        showManpowerButton={false}
                        manPower_monthly={manPower_monthly}
                        memo={issuedMemo}
                        setMemo={() => {}}
                        isEditableMemo={false}
                        copyNurses={precepteeStats.statsNurses}
                        shiftLookupMaps={shiftLookupMaps}
                        showPrecepteeBadge={false}
                        role={
                          isAdmin
                            ? "admin"
                            : isHeadNurse
                              ? "head_nurse"
                              : "nurse"
                        }
                      />
                    )}
                  </div>
                )}
              </div>

              <div className="relative flex items-center justify-between gap-[10px] ">
                {!isRosterSnapshotLoading && displayNurses.length > 0 && (
                  <>
                    <TableHeader
                      layoutConfig={viewLayoutConfig}
                      defaultShiftCode={shiftsForView}
                      copyNurses={displayNurses}
                      setCopyNurses={setCopyNursesForHeader}
                      currentYear={currentScheduleDate.year}
                      currentMonth={currentScheduleDate.month}
                      holidayDates={holidayDates}
                    />
                    <div className="relative flex size-10 shrink-0 items-end">
                      <div className="absolute -bottom-[17px] left-1/2 -translate-x-1/2 flex rounded-full bg-white">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              className={cn(
                                "group flex size-7 shrink-0 items-center justify-center rounded-full border-0 bg-transparent p-0",
                                "transition-opacity hover:opacity-90",
                                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-offset-1"
                              )}
                              aria-pressed={showPerShiftStats}
                              aria-label={
                                showPerShiftStats
                                  ? "개별 근무코드 통계 숨기기"
                                  : "개별 근무코드 통계 보기"
                              }
                              onClick={() =>
                                setShowPerShiftStats((prev) => !prev)
                              }
                            >
                              <img
                                src="/asset/icon/config/icon_1.svg"
                                alt=""
                                aria-hidden
                                className={cn(
                                  "size-7 object-contain transition-[filter,transform] duration-200 ease-out brightness-100 group-hover:brightness-[0.55]",
                                  showPerShiftStats ? "rotate-90" : "-rotate-90"
                                )}
                                draggable={false}
                              />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>
                              {showPerShiftStats
                                ? "개별 근무코드 통계 숨기기"
                                : "개별 근무코드 통계 보기"}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </div>
                  </>
                )}
                {showPerShiftStats && (
                  <div className="absolute bottom-0 left-full animate-in fade-in duration-200 pr-2">
                    <RosterShiftLegendChips
                      displayShifts={rosterViewShiftLegend}
                      defaultShiftCode={shiftsForView}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="relative  px-5 pb-30 ">
            <div
              className={cn(
                "animate-in fade-in-0 duration-300 transition-opacity relative"
              )}
            >
              <div className="relative">
                <TableBodyView
                  teamView={teamView && hasTeams}
                  issuedTeams={issuedTeams}
                  changeCells={changes.notice?.cells}
                  layoutConfig={viewLayoutConfig}
                  shiftInfo={shiftsForView}
                  shiftLookupMaps={shiftLookupMaps}
                  nurses={displayNurses}
                  year={currentScheduleDate.year}
                  month={currentScheduleDate.month}
                  defaultShiftCode={shiftsForView}
                  showPerShiftStats={showPerShiftStats}
                  issuedSnapshotGroupId={issuedSnapshotGroupId}
                  currentGroupId={issuedSnapshotGroupId || null}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RosterViewAllTabContent;
